// Familie-functies (w4; docs/DESIGN.md §8.F), handgeschreven zoals index.ts en gelijk aan de API
// (apps/api/src/routes/v1/app/family, kiosk/family.ts, share.ts). Kiosk-veilig: alleen types en
// fetch; openapi.contract.test.ts bewaakt dat elke aanroep hieronder in de OpenAPI bestaat.
const id = (value) => encodeURIComponent(value);
/** Querystring zonder lege waarden (zelfde naam als in index.ts: de contracttest stript hem). */
function query(params) {
    const parts = [];
    for (const key of Object.keys(params)) {
        const value = params[key];
        if (value !== undefined)
            parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
    return parts.length > 0 ? `?${parts.join('&')}` : '';
}
export function createFamilyClient(t) {
    const get = (path) => t.request('GET', path);
    const post = (path, body = {}) => t.request('POST', path, body);
    const patch = (path, body) => t.request('PATCH', path, body);
    const put = (path, body) => t.request('PUT', path, body);
    const del = (path, body) => t.request('DELETE', path, body);
    return {
        /** Ouderapp (fb_sess). */
        app: {
            highlights: (from, to) => get(`/v1/app/family/highlights${query({ from, to })}`),
            setLayers: (patchIn) => put('/v1/app/family/layers', patchIn),
            setBirthDate: (memberId, birthDate, ifVersion) => put(`/v1/app/members/${id(memberId)}/birth-date`, {
                birth_date: birthDate,
                ...(ifVersion !== undefined ? { if_version: ifVersion } : {}),
            }),
            /** Voorleesknop per gezinslid; on = null zet hem terug op de standaard. */
            readAloud: () => get('/v1/app/read-aloud'),
            setReadAloud: (memberId, patch) => put(`/v1/app/read-aloud/${id(memberId)}`, patch),
            setReadAloudLocale: (speechLocale) => put('/v1/app/read-aloud', { speech_locale: speechLocale }),
            wishes: (reveal = false) => get(`/v1/app/wishes${query({ reveal: reveal ? 'true' : undefined })}`),
            createWish: (input) => post('/v1/app/wishes', input),
            updateWish: (wishId, input) => patch(`/v1/app/wishes/${id(wishId)}`, input),
            removeWish: (wishId) => del(`/v1/app/wishes/${id(wishId)}`),
            restoreWish: (wishId) => post(`/v1/app/wishes/${id(wishId)}/restore`),
            shareList: (listId) => post(`/v1/app/wish-lists/${id(listId)}/share`),
            unshareList: (listId) => del(`/v1/app/wish-lists/${id(listId)}/share`),
            links: () => get('/v1/app/links'),
            invite: (input) => post('/v1/app/links', input),
            previewLink: (input) => post('/v1/app/links/preview', input),
            acceptLink: (input) => post('/v1/app/links/accept', input),
            updateLink: (linkId, input) => patch(`/v1/app/links/${id(linkId)}`, input),
            revokeLink: (linkId) => del(`/v1/app/links/${id(linkId)}`),
            linkView: (linkId) => get(`/v1/app/links/${id(linkId)}/view`),
            claimLinkedWish: (linkId, wishId, input = {}) => post(`/v1/app/links/${id(linkId)}/wishes/${id(wishId)}/claim`, input),
            releaseLinkedWish: (linkId, wishId) => del(`/v1/app/links/${id(linkId)}/wishes/${id(wishId)}/claim`),
            contact: () => get('/v1/app/contact'),
            setContact: (input) => put('/v1/app/contact', input),
            conversations: () => get('/v1/app/conversations'),
            thread: (convId) => get(`/v1/app/conversations/${id(convId)}`),
            send: (convId, input) => post(`/v1/app/conversations/${id(convId)}/messages`, input),
            createConversation: (input) => post('/v1/app/conversations', input),
            updateConversation: (convId, input) => patch(`/v1/app/conversations/${id(convId)}`, input),
            archiveConversation: (convId) => del(`/v1/app/conversations/${id(convId)}`),
            hideMessage: (messageId) => post(`/v1/app/messages/${id(messageId)}/hide`),
            voiceUrl: (messageId) => `${t.baseUrl}/v1/app/messages/${id(messageId)}/voice`,
            linkedThread: (linkId, convId) => get(`/v1/app/links/${id(linkId)}/conversations/${id(convId)}`),
            linkedSend: (linkId, convId, input) => post(`/v1/app/links/${id(linkId)}/conversations/${id(convId)}/messages`, input),
            linkedVoiceUrl: (linkId, messageId) => `${t.baseUrl}/v1/app/links/${id(linkId)}/messages/${id(messageId)}/voice`,
            feeds: () => get('/v1/app/calendar-feeds'),
            createFeed: (input) => post('/v1/app/calendar-feeds', input),
            updateFeed: (feedId, input) => patch(`/v1/app/calendar-feeds/${id(feedId)}`, input),
            removeFeed: (feedId) => del(`/v1/app/calendar-feeds/${id(feedId)}`),
            refreshFeed: (feedId) => post(`/v1/app/calendar-feeds/${id(feedId)}/refresh`),
            feedItems: (feedId, onlyReview = false) => get(`/v1/app/calendar-feeds/${id(feedId)}/items${query({ route: onlyReview ? 'review' : undefined })}`),
            setFeedOverlay: (feedId, input) => put(`/v1/app/calendar-feeds/${id(feedId)}/overlay`, input),
        },
        /** Deelpagina (anoniem; token uit het URL-fragment als header). */
        share: {
            list: (token, secret) => t.request('GET', '/v1/share/wishes', undefined, {
                'X-Share-Token': token,
                ...(secret ? { 'X-Share-Secret': secret } : {}),
            }),
            claim: (token, wishId, input) => t.request('POST', `/v1/share/wishes/${id(wishId)}/claim`, input, { 'X-Share-Token': token }),
            release: (token, wishId, secret) => t.request('POST', `/v1/share/wishes/${id(wishId)}/release`, { secret }, {
                'X-Share-Token': token,
            }),
        },
        /** Kiosk (fb_dev). */
        kiosk: {
            wishes: () => get('/v1/kiosk/wishes'),
            addWish: (input) => post('/v1/kiosk/wishes', input),
            removeWish: (wishId, memberId) => post(`/v1/kiosk/wishes/${id(wishId)}/remove`, {
                member_id: memberId,
            }),
            conversations: () => get('/v1/kiosk/conversations'),
            thread: (convId, memberId) => get(`/v1/kiosk/conversations/${id(convId)}${query({ member_id: memberId })}`),
            send: (convId, input) => post(`/v1/kiosk/conversations/${id(convId)}/messages`, input),
            voiceUrl: (messageId, memberId) => `${t.baseUrl}/v1/kiosk/messages/${id(messageId)}/voice?member_id=${id(memberId)}`,
            summary: () => get('/v1/kiosk/family/summary'),
        },
    };
}
//# sourceMappingURL=family.js.map